import React, { useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    TextField
} from '@mui/material';
import { WelcomeBoardLayout } from '../../../../common/interfaces';
import { parseGridTemplate } from '../utils';

interface GridConfigDialogProps {
    open: boolean;
    layout: WelcomeBoardLayout;
    onClose: () => void;
    onSave: (newLayout: WelcomeBoardLayout) => void;
    onAlert: (message: string) => void;
}

export const GridConfigDialog: React.FC<GridConfigDialogProps> = ({
    open,
    layout,
    onClose,
    onSave,
    onAlert
}) => {
    const [tempGridConfig, setTempGridConfig] = useState({
        columns: parseGridTemplate(layout.gridColumnsTemplate),
        rows: parseGridTemplate(layout.gridRowsTemplate)
    });

    const handleSave = useCallback(() => {
        const newLayout = {
            ...layout,
            gridColumnsTemplate: `repeat(${tempGridConfig.columns}, minmax(0, 1fr))`,
            gridRowsTemplate: `repeat(${tempGridConfig.rows}, minmax(0, 1fr))`,
            welcomeBoardItems: [] // Clear widgets when changing grid size
        };
        onSave(newLayout);
        onClose();
        onAlert('Grid configuration updated. All widgets have been cleared.');
    }, [layout, tempGridConfig, onSave, onClose, onAlert]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Configure Grid Dimensions</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        label="Grid Columns"
                        type="number"
                        value={tempGridConfig.columns}
                        onChange={(e) => setTempGridConfig(prev => ({
                            ...prev,
                            columns: Math.max(1, Math.min(10, parseInt(e.target.value) || 1))
                        }))}
                        inputProps={{ min: 1, max: 10 }}
                        sx={{ mb: 2 }}
                        helperText="Number of columns (1-10)"
                    />
                    <TextField
                        fullWidth
                        label="Grid Rows"
                        type="number"
                        value={tempGridConfig.rows}
                        onChange={(e) => setTempGridConfig(prev => ({
                            ...prev,
                            rows: Math.max(1, Math.min(10, parseInt(e.target.value) || 1))
                        }))}
                        inputProps={{ min: 1, max: 10 }}
                        helperText="Number of rows (1-10)"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                >
                    Apply Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};